import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// --- ADONISIAN MASTER CONFIGURATION ---
const PRECESSION_RATE = 71.6; // Years per 1 degree of movement
const PISCES_START_YEAR = -316; // 316 BC

const ADONISIAN_AGES = [
  { name: "Aries", start: 28.40, end: 53.48, symbol: "♈︎", element: "Fire", span: 25.08 },
  { name: "Taurus", start: 53.48, end: 90.25, symbol: "♉︎", element: "Earth", span: 36.77 },
  { name: "Gemini", start: 90.25, end: 118.02, symbol: "♊︎", element: "Air", span: 27.77 },
  { name: "Cancer", start: 118.02, end: 138.16, symbol: "♋︎", element: "Water", span: 20.14 },
  { name: "Leo", start: 138.16, end: 173.86, symbol: "♌︎", element: "Fire", span: 35.70 },
  { name: "Virgo", start: 173.86, end: 218.01, symbol: "♍︎", element: "Earth", span: 44.15 },
  { name: "Libra", start: 218.01, end: 241.13, symbol: "♎︎", element: "Air", span: 23.12 },
  { name: "Scorpio", start: 241.13, end: 247.72, symbol: "♏︎", element: "Water", span: 6.59 },
  { name: "Ophiuchus", start: 247.72, end: 266.38, symbol: "⛎︎", element: "Aether", span: 18.66 },
  { name: "Sagittarius", start: 266.38, end: 299.73, symbol: "♐︎", element: "Fire", span: 33.35 },
  { name: "Capricorn", start: 299.73, end: 327.42, symbol: "♑︎", element: "Earth", span: 27.69 },
  { name: "Aquarius", start: 327.42, end: 351.83, symbol: "♒︎", element: "Air", span: 24.41 },
  { name: "Pisces", start: 351.83, end: 28.40, symbol: "♓︎", element: "Water", span: 36.57 }
];

const linkStyle = { 
  color: '#0d1117', 
  backgroundColor: '#ffca28', 
  padding: '10px 20px', 
  borderRadius: '4px', 
  textDecoration: 'none', 
  marginTop: '40px', 
  display: 'inline-block', 
  fontWeight: 'bold', 
  letterSpacing: '1px' 
};

export default function GreatYearTracker() {
  const [sliderYear, setSliderYear] = useState(new Date().getFullYear());
  const [activeWheelSign, setActiveWheelSign] = useState(ADONISIAN_AGES.find(s => s.name === "Pisces"));

  // --- SCROLL TO TOP ON LOAD ---
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // --- MATH FOR TIMELINE SLIDER ---
  const piscesDuration = Math.round(36.57 * PRECESSION_RATE); // 2,618 years
  const jubileeYear = PISCES_START_YEAR + piscesDuration; // 2302 AD
  
  const minYear = -1000;
  const maxYear = 4000;
  const totalTimelineSpan = maxYear - minYear; // 5000 years

  const ariesWidth = ((PISCES_START_YEAR - minYear) / totalTimelineSpan) * 100;
  const piscesWidth = (piscesDuration / totalTimelineSpan) * 100;
  const aquariusWidth = ((maxYear - jubileeYear) / totalTimelineSpan) * 100;

  let activeSign = "Aries";
  let activeColor = "#ff4081";
  let degreesMoved = 0;
  let signSpan = 25.08;

  if (sliderYear >= PISCES_START_YEAR && sliderYear < jubileeYear) {
    activeSign = "Pisces";
    activeColor = "#00d4ff";
    degreesMoved = (sliderYear - PISCES_START_YEAR) / PRECESSION_RATE;
    signSpan = 36.57;
  } else if (sliderYear >= jubileeYear) {
    activeSign = "Aquarius";
    activeColor = "#00ff41";
    degreesMoved = (sliderYear - jubileeYear) / PRECESSION_RATE;
    signSpan = 24.41;
  } else {
    degreesMoved = (PISCES_START_YEAR - sliderYear) / PRECESSION_RATE;
  }

  const formatYear = (yr) => (yr < 0 ? `${Math.abs(yr)} BC` : `${yr} AD`);
  const isJubileeTriggered = sliderYear === jubileeYear;

  // Helper for the wheel rotation math
  const getRotatedDegree = (deg) => (deg + 90 + 360) % 360;

  return (
<div style={{ width: '100%', maxWidth: '100vw', margin: '0 auto', padding: '20px 15px', boxSizing: 'border-box', color: '#c9d1d9', fontFamily: 'monospace', overflowX: 'hidden' }}>
      
      {/* 👇 THE GREAT YEAR SEO SHIELD 👇 */}
      <Helmet>
        <title>Sign Ages & The Great Year | Adonisian Astrology</title>
        <meta name="description" content="Decode the precession of the equinoxes. Explore the true astronomical Sign Ages and where humanity currently sits within the Great Year macro-cycle." />
      </Helmet>

      {/* --- THE GROUNDBREAKING MANIFESTO --- */}
      <div style={{ textAlign: 'left', marginBottom: '60px', lineHeight: '1.8' }}>
        <h2 style={{ color: '#ffca28', fontSize: '2.5rem', textTransform: 'uppercase', letterSpacing: '2px', textAlign: 'center', marginBottom: '30px' }}>
          The Great Solar Year & The Precession Matrix
        </h2>
        
        <p style={{ fontSize: '1.1rem' }}>
          The New Age movement has commercialized a comforting illusion. They claim humanity has already entered the "Age of Aquarius." But the cosmos operates on strict, observable mathematical law, not wishful thinking. By mapping the exact, real-time boundaries of the 13 celestial constellations against the physical wobble of the Earth, the Adonisian Matrix exposes the true timeline of human ascendance.
        </p>

        <h3 style={{ color: '#00ff41', marginTop: '40px', textTransform: 'uppercase' }}>The 71.6 Constant & The Unequal Sky</h3>
        <p>
          The Earth wobbles on its axis, causing the Vernal Equinox to slowly drift backward through the zodiac. Standard pop-astrology artificially chops this cycle into 12 equal slices of 2,150 years. <strong>But the sky is not equal.</strong> 
          <br/><br/>
          Because the constellation of Virgo spans a massive 44° of the ecliptic and Scorpio spans only 6.5°, the "Ages" cannot be uniform. It takes the Equinox exactly <strong>71.6 years to traverse a single degree</strong> of physical space.
        </p>

        <h3 style={{ color: '#00d4ff', marginTop: '40px', textTransform: 'uppercase' }}>The 316 BC Anchor & The 2302 Jubilee</h3>
        <p>
          Based on true astronomical boundaries, the Vernal Equinox officially crossed into the constellation of Pisces in the year <strong>316 BC</strong>. This is not a coincidence. The age symbolized by the Christian fish began exactly in the year 316, physically mirroring the foundational scripture of that very age (John 3:16).
        </p>

        <blockquote style={{ borderLeft: '3px solid #ffca28', paddingLeft: '20px', margin: '30px 0', color: '#ffca28', fontStyle: 'italic', fontSize: '1.2rem' }}>
          "Adding the true 2,618-year span of Pisces to the 316 BC anchor reveals the undeniable truth. The Great Adonisian Equinox Jubilee—the true dawn of Aquarius—will trigger in <strong>2302 AD</strong>. We are not in Aquarius. We are in the final contraction of Pisces."
        </blockquote>
      </div>

      {/* --- WIDGET 1: THE INTERACTIVE WHEEL --- */}
     <div style={{ width: '100%', background: '#0d1117', padding: '20px', borderRadius: '12px', border: '1px solid #30363d', marginBottom: '50px', display: 'flex', flexWrap: 'wrap', gap: '30px', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
  
  <div style={{ position: 'relative', width: '100%', maxWidth: '350px', aspectRatio: '1/1' }}>
   <p style={{ position: 'absolute', top: '-30px', width: '100%', textAlign: 'center', color: '#00ff41', fontSize: '0.85rem', letterSpacing: '1px' }}>
            SELECT A NODE TO DECODE THE AGE
          </p>
            <svg viewBox="-20 -20 140 140" aria-label="The Great Solar Year Precession Matrix and True Astronomical Sign Ages" role="img" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
              <title>Great Year Precession Matrix</title>
              <circle cx="50" cy="50" r="48" fill="none" stroke="#1f2428" strokeWidth="0.5" />
            
            {ADONISIAN_AGES.map((sign) => {
              const rotatedStart = getRotatedDegree(sign.start);
              const rotatedEnd = getRotatedDegree(sign.end);
              const startAngle = (rotatedStart - 90) * (Math.PI / 180);
              const endAngle = (rotatedEnd - 90) * (Math.PI / 180);
              const midAngle = ((rotatedStart + (rotatedEnd < rotatedStart ? rotatedEnd + 360 : rotatedEnd)) / 2 - 90) * (Math.PI / 180);
              
              const sx = 50 + 40 * Math.cos(midAngle);
              const sy = 50 + 40 * Math.sin(midAngle);
              const isActive = activeWheelSign.name === sign.name;

              return (
                <g key={sign.name} style={{ cursor: 'pointer' }} onClick={() => setActiveWheelSign(sign)}>
                  <path d={`M 50 50 L ${50 + 48 * Math.cos(startAngle)} ${50 + 48 * Math.sin(startAngle)}`} stroke="#1f2428" strokeWidth="0.3" />
                  <circle cx={sx} cy={sy} r="6" fill={isActive ? "#ffca28" : "transparent"} stroke={isActive ? "#ffca28" : "#30363d"} strokeWidth="0.5" style={{ transition: 'all 0.3s ease' }} />
                  <text x={sx} y={sy} fontSize="4" textAnchor="middle" dominantBaseline="middle" fill={isActive ? "#000" : "#00d4ff"}>
                    {sign.symbol}
                  </text>
                </g>
              );
            })}

            {/* Inner Core Display */}
            <circle cx="50" cy="50" r="28" fill="#05070a" stroke="#00d4ff" strokeWidth="0.5" strokeDasharray="1 1" />
            <text x="50" y="44" fontSize="3" fill="#8b949e" textAnchor="middle" letterSpacing="0.5">AGE OF</text>
            <text x="50" y="52" fontSize="5.5" fill="white" textAnchor="middle" fontWeight="bold" textTransform="uppercase">{activeWheelSign.name}</text>
            <text x="50" y="59" fontSize="2.5" fill="#ffca28" textAnchor="middle">{activeWheelSign.element} Node</text>
          </svg>
        </div>

        {/* THE DATA READOUT */}
        <div style={{ flex: '1 1 300px', textAlign: 'center' }}>
          <h3 style={{ color: '#00d4ff', borderBottom: '1px solid #30363d', paddingBottom: '15px', marginBottom: '20px' }}>
            {activeWheelSign.symbol} THE AGE OF {activeWheelSign.name.toUpperCase()}
          </h3>

        <div style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', textAlign: 'left', boxSizing: 'border-box' }}>            <div style={{ background: '#05070a', padding: '15px', borderRadius: '6px', border: '1px solid #1f2428' }}>
              <span style={{ color: '#8b949e', fontSize: '0.8rem', display: 'block' }}>Ecliptic Span</span>
              <span style={{ color: 'white', fontSize: '1.4rem', fontWeight: 'bold' }}>{activeWheelSign.span}°</span>
            </div>

            <div style={{ background: '#05070a', padding: '15px', borderRadius: '6px', border: '1px solid #1f2428' }}>
              <span style={{ color: '#8b949e', fontSize: '0.8rem', display: 'block' }}>Precession Math</span>
              <span style={{ color: '#00ff41', fontSize: '1.1rem', fontWeight: 'bold' }}>{activeWheelSign.span} × 71.6</span>
            </div>
          </div>

          <div style={{ width: '100%', background: '#05070a', padding: '20px', borderRadius: '6px', border: '1px solid #ffca28', marginTop: '15px', boxSizing: 'border-box' }}>            <span style={{ color: '#ffca28', fontSize: '0.9rem', display: 'block', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '5px' }}>True Age Duration</span>
            <span style={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold' }}>{Math.round(activeWheelSign.span * PRECESSION_RATE)} <span style={{fontSize: '1rem', color: '#8b949e'}}>Years</span></span>
          </div>
        </div>
      </div>

      {/* --- THE MATHEMATICAL PROOF TABLE --- */}
      <h3 style={{ color: '#ffffff', textAlign: 'center', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '2px' }}>
        The Mathematical Proof
      </h3>
      <div style={{ width: '100%', background: '#0d1117', borderRadius: '12px', border: '1px solid #30363d', overflowX: 'auto', marginBottom: '60px', boxSizing: 'border-box' }}>        
        <div style={{ minWidth: '600px',display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', background: '#1f2428', padding: '15px 20px', fontWeight: 'bold', color: '#8b949e', fontSize: '0.9rem', textTransform: 'uppercase' }}>
          <span>Zodiac Sign</span>
          <span>Ecliptic Span</span>
          <span>Age Duration (Span × 71.6)</span>
        </div>
        {ADONISIAN_AGES.map((sign, index) => {
          const duration = Math.round(sign.span * PRECESSION_RATE);
          return (
            <div key={sign.name} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', padding: '15px 20px', borderBottom: '1px solid #1f2428', background: index % 2 === 0 ? '#0d1117' : '#05070a', alignItems: 'center' }}>
              <span style={{ color: '#00d4ff', fontWeight: 'bold' }}>{sign.symbol} {sign.name}</span>
              <span style={{ color: 'white' }}>{sign.span}°</span>
              <span style={{ color: '#ffca28', fontFamily: 'monospace', fontSize: '1.1rem' }}>
                <span style={{ color: '#8b949e', fontSize: '0.9rem' }}>{sign.span} × 71.6 = </span>
                {duration.toLocaleString()} years
              </span>
            </div>
          );
        })}
      </div>

      {/* --- WIDGET 1: THE INTERACTIVE TIMELINE SLIDER --- */}
      <div style={{ background: '#0d1117', padding: '40px', borderRadius: '12px', border: '1px solid #30363d', marginBottom: '50px' }}>
        <h2 style={{ color: 'white', textAlign: 'center', marginBottom: '20px', letterSpacing: '1px' }}>
          {isJubileeTriggered ? "✨ THE GREAT JUBILEE ✨" : "INTERACTIVE EPOCH TRACKER"}
        </h2>
        
        {/* DYNAMIC READOUT */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '30px' }}>
          <div style={{ flex: '1 1 30%', background: '#05070a', border: `1px solid ${activeColor}`, padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
            <h4 style={{ color: '#8b949e', margin: '0 0 5px 0', fontSize: '0.9rem' }}>Selected Year</h4>
            <span style={{ fontSize: '1.8rem', color: 'white', fontWeight: 'bold' }}>{formatYear(sliderYear)}</span>
          </div>
          <div style={{ flex: '1 1 30%', background: '#05070a', border: `1px solid ${activeColor}`, padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
            <h4 style={{ color: '#8b949e', margin: '0 0 5px 0', fontSize: '0.9rem' }}>Active Age</h4>
            <span style={{ fontSize: '1.8rem', color: activeColor, fontWeight: 'bold', textTransform: 'uppercase' }}>{activeSign}</span>
          </div>
          <div style={{ flex: '1 1 30%', background: '#05070a', border: `1px solid ${activeColor}`, padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
            <h4 style={{ color: '#8b949e', margin: '0 0 5px 0', fontSize: '0.9rem' }}>Equinox Coordinate</h4>
            <span style={{ fontSize: '1.2rem', color: 'white', display: 'block', marginTop: '10px' }}>{degreesMoved.toFixed(2)}° / {signSpan}°</span>
          </div>
        </div>

        {/* VISUAL TRACK */}
        <div style={{ position: 'relative', width: '100%', height: '35px', background: '#111', borderRadius: '6px', display: 'flex', overflow: 'hidden', marginBottom: '20px' }}>
          <div style={{ width: `${ariesWidth}%`, minWidth: 0, height: '100%', background: '#ff4081', opacity: 0.6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: 'white', fontWeight: 'bold', overflow: 'hidden' }}>ARIES</div>
          <div style={{ width: `${piscesWidth}%`, minWidth: 0, height: '100%', background: '#00d4ff', opacity: 0.6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: 'white', fontWeight: 'bold', overflow: 'hidden' }}>PISCES</div>
          <div style={{ width: `${aquariusWidth}%`, minWidth: 0, height: '100%', background: '#00ff41', opacity: 0.6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: 'white', fontWeight: 'bold', overflow: 'hidden' }}>AQUA</div>

          {/* THE SCRUBBER POSITION MARKER */}
          <div style={{
            position: 'absolute',
            left: `${((sliderYear - minYear) / totalTimelineSpan) * 100}%`,
            top: 0,
            bottom: 0,
            width: '4px',
            background: 'white',
            boxShadow: '0 0 10px white',
            zIndex: 10,
            transform: 'translateX(-50%)'
          }}></div>
        </div>

        {/* SLIDER INPUT */}
        <input 
          type="range" 
          min={minYear} 
          max={maxYear} 
          value={sliderYear} 
          onChange={(e) => setSliderYear(parseInt(e.target.value))}
          style={{ width: '100%', cursor: 'pointer', accentColor: activeColor }}
        />
        
        {/* QUICK JUMP BUTTONS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <button onClick={() => setSliderYear(new Date().getFullYear())} style={{ background: 'transparent', border: '1px solid #00d4ff', color: '#00d4ff', padding: '8px 15px', borderRadius: '4px', cursor: 'pointer' }}>
            Jump to Present Day
          </button>
          <button onClick={() => setSliderYear(jubileeYear)} style={{ background: '#ffca28', border: 'none', color: '#000', padding: '8px 15px', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>
            Trigger 2302 Jubilee
          </button>
        </div>
      </div>
<footer style={{ textAlign: 'center', color: '#484f58', fontSize: '0.8rem', padding: '20px 0' }}>
        Calculations derived from Swiss Ephemeris Ecliptic Boundaries & 71.6yr/deg Precession Rate.
      </footer>
      <div style={{ textAlign: 'center', paddingBottom: '30px' }}>
        <Link to="/" style={linkStyle}>The Portal 📟</Link>
      </div>

    </div>
  );
}